import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  useWriteContract,
  useWaitForTransactionReceipt,
  useReadContract,
  useAccount,
} from 'wagmi';
import { parseUnits } from 'viem';
import { showToast } from '@/utils/toast';
import {
  useGetAllChestTypes,
  useSeiBalance,
  useUnopenedChests,
  useVerifyChestPurchase,
} from '@hooks';
import { useNetworkSwitch } from '@/hooks/useNetworkSwitch';
import { PurchaseChestModal } from '@/components/modals/PurchaseChestModal';
import { InventoryModal, VerificationLoadingModal } from '@/components/modals';
import { Loading } from '@/components/common';
import { SHOP_CONTRACT_ADDRESS, SHOP_CONTRACT_ABI } from '@/config/contracts';
import { useAuthStore } from '@app/stores';
import type { Chest, ChestRarity } from '@types';
import shopChestNormal from '@/assets/shop/shop-chest-normal.webp';
import shopChestPlatinum from '@/assets/shop/shop-chest-platinum.webp';
import shopChestRare from '@/assets/shop/shop-chest-rare.webp';
import shopChestLegendary from '@/assets/shop/shop-chest-legendary.webp';
import shopChestBackground from '@/assets/shop/shop-chest-background.webp';
import seiToken from '@/assets/header/sei.webp';
import inventory from '@/assets/shop/inventory.webp';

const chestImageMap: Record<ChestRarity, string> = {
  normal: shopChestNormal,
  platinum: shopChestPlatinum,
  rare: shopChestRare,
  legendary: shopChestLegendary,
};

interface ChestCardProps {
  chest: Chest;
  onClick: () => void;
  isLast?: boolean;
  disabled?: boolean;
}

const ChestCard = ({
  chest,
  onClick,
  isLast = false,
  disabled = false,
}: ChestCardProps) => {
  return (
    <motion.div
      className={`relative p-1 ${isLast ? '' : 'flex-1'} ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      }`}
      style={{
        background: 'linear-gradient(180deg, #8674EA 0%, #A04BC2 100%)',
        borderRadius: '20px',
        boxShadow: '0px 4px 0px #401E4D',
      }}
      onClick={onClick}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
    >
      {/* Card Background Layer - creates 4px gap */}
      <div
        className="p-1 w-full h-full"
        style={{
          background: 'linear-gradient(180deg, #A155D0 0%, #FBBB52 100%)',
          borderRadius: '16px',
        }}
      >
        {/* Inner Border Layer */}
        <div
          className="relative p-1 w-full h-full"
          style={{
            background:
              'linear-gradient(to bottom right, #FF69E9 0%, #FEAC3F 100%)',
            borderRadius: '12px',
          }}
        >
          {/* Inner Container */}
          <div
            className={`relative overflow-hidden w-full ${
              isLast ? 'h-[226px]' : 'h-[155px]'
            }`}
            style={{
              borderRadius: '8px',
              boxShadow: 'inset 0px 1px 4px 5px rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Background Pattern */}
            <img
              src={shopChestBackground}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />

            {/* Gradient Overlay */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(161, 85, 208, 0.1) 0%, rgba(251, 187, 82, 0.4) 100%)',
              }}
            />

            {/* Chest Name */}
            <div
              className={`absolute left-2 right-2 text-center ${
                isLast ? 'text-2xl top-4' : 'text-lg top-2'
              }`}
              style={{
                fontWeight: 400,
                lineHeight: '21px',
                color: '#FFFFFF',
                WebkitTextStroke: '0.5px #000000',
                textShadow: '0.5px 0.5px 0px #000000',
              }}
            >
              {chest.name}
            </div>

            {/* Chest Image */}
            <div
              className={`absolute left-1/2 top-1/2 -translate-x-1/2 ${
                isLast
                  ? 'w-28 -translate-y-[60%]'
                  : 'w-[72px] h-[72px] -translate-y-[60%]'
              }`}
            >
              <img
                src={chest.imageUrl}
                alt={chest.name}
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>

            {/* Price Container */}
            <div
              className={`absolute bottom-0 left-0 right-0 flex flex-row justify-center items-center gap-2 ${
                isLast ? 'py-4 h-16 mt-3' : 'py-2 h-11 mt-2'
              }`}
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '0 0 8px 8px',
              }}
            >
              <img
                src={seiToken}
                alt="SEI"
                className={isLast ? 'w-8 h-8' : 'w-6 h-6'}
              />
              <span
                className={`font-bold ${isLast ? 'text-3xl' : 'text-xl'}`}
                style={{
                  color: '#FFE500',
                  WebkitTextStroke: '0.5px #000000',
                  textShadow: '1px 1px 0px #000000',
                }}
              >
                {chest.price}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const ShopContent = () => {
  const { address } = useAccount();
  const { chestTypes, isLoading, isError } = useGetAllChestTypes();
  const { balance: seiBalance, refetch: refetchSeiBalance } = useSeiBalance();
  const {
    unopenedChests,
    total: unopenedChestsCount,
    refetch: refetchUnopenedChests,
  } = useUnopenedChests();
  const {
    isCorrectNetwork,
    requiredNetwork,
    switchToCorrectNetwork,
    isSwitching,
  } = useNetworkSwitch();
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [selectedChest, setSelectedChest] = useState<Chest | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [verifiedHashes, setVerifiedHashes] = useState<Set<string>>(new Set());

  // Get player daily chest info
  const { data: dailyChestInfo, refetch: refetchDailyChestInfo } =
    useReadContract({
      address: SHOP_CONTRACT_ADDRESS,
      abi: SHOP_CONTRACT_ABI,
      functionName: 'getPlayerDailyChestInfo',
      args: address ? [address] : undefined,
      query: {
        enabled: !!address,
      },
    });

  // Parse daily chest info
  const dailyChestData = dailyChestInfo as [bigint, bigint] | undefined;
  const purchasedToday = dailyChestData
    ? Number(dailyChestData[0]?.toString() || 0)
    : 0;
  const remainingToday = dailyChestData
    ? Number(dailyChestData[1]?.toString() || 0)
    : 5; // Default to 5 if not loaded yet
  const dailyLimit = purchasedToday + remainingToday;
  const hasReachedLimit = remainingToday === 0;

  // Contract write hook
  const { writeContract, data: hash, isPending } = useWriteContract();

  // Wait for transaction receipt
  const { isLoading: isConfirming } = useWaitForTransactionReceipt({
    hash,
  });

  // Verification polling hook with 10 retry attempts
  const verifyPurchaseMutation = useVerifyChestPurchase({
    maxAttempts: 10,
    pollingInterval: 3000,
    onSuccess: () => {
      console.log('[ShopContent] ✅ Purchase verified successfully:', {
        chest: selectedChest?.name,
        hash,
      });

      showToast.success(`Successfully purchased ${selectedChest?.name}!`, {
        style: {
          background: '#4CAF50',
          color: '#fff',
        },
      });

      // Mark hash as verified
      if (hash) {
        setVerifiedHashes((prev) => new Set(prev).add(hash));
      }

      console.log('[ShopContent] Refreshing data after successful purchase...');
      Promise.all([
        refetchSeiBalance(),
        refetchUnopenedChests(),
        refetchDailyChestInfo(),
        useAuthStore.getState().validateAndUpdateUser(),
      ])
        .then(() => {
          console.log('[ShopContent] Data refresh completed');
        })
        .catch((err) =>
          console.error('[ShopContent] Failed to refresh data:', err)
        );

      // Reset state
      setIsPurchasing(false);
    },
    onError: (error) => {
      console.error('[ShopContent] ❌ Purchase verification failed:', {
        error: error.message,
        chest: selectedChest?.name,
        hash,
      });

      showToast.error(
        error.message ||
          'Failed to verify purchase. Please check your inventory.',
        {
          style: {
            background: '#f44336',
            color: '#fff',
          },
          duration: 6000,
        }
      );

      // Mark hash as verified
      if (hash) {
        setVerifiedHashes((prev) => new Set(prev).add(hash));
      }

      console.log('[ShopContent] Refreshing data after failed verification...');
      // Still refetch to get latest state
      Promise.all([
        refetchSeiBalance(),
        refetchUnopenedChests(),
        refetchDailyChestInfo(),
        useAuthStore.getState().validateAndUpdateUser(),
      ])
        .then(() => {
          console.log('[ShopContent] Data refresh completed');
        })
        .catch((err) =>
          console.error('[ShopContent] Failed to refresh data:', err)
        );

      // Reset state
      setIsPurchasing(false);
    },
  });

  // Map chestId to rarity for image selection
  const chestIdToRarity: Record<number, ChestRarity> = {
    1: 'normal',
    2: 'platinum',
    3: 'rare',
    4: 'legendary',
  };

  const chests: Chest[] = chestTypes
    .filter((ct) => ct.isActive) // Only show active chests
    .map((ct) => {
      const chestIdNum = Number(ct.chestId);
      const rarity = chestIdToRarity[chestIdNum] || 'normal';
      // Convert from wei to SEI (divide by 10^18)
      const priceInSei = Number(ct.price) / 1e18;

      return {
        id: `chest-${ct.chestId}`,
        name: ct.name,
        rarity: rarity,
        price: priceInSei,
        imageUrl: chestImageMap[rarity],
        description: `Open to receive random rewards!`,
      } as Chest;
    });

  // Log onchain data
  useEffect(() => {
    console.log('[ShopContent] 📊 Onchain Data:', {
      // User info
      walletAddress: address,
      seiBalance: seiBalance,

      // Daily chest info from contract
      dailyChestInfo: {
        raw: dailyChestData,
        purchasedToday,
        remainingToday,
        dailyLimit,
        hasReachedLimit,
      },

      // Chest types from contract
      chestTypes: {
        raw: chestTypes,
        isLoading,
        isError,
        count: chestTypes?.length || 0,
      },

      // Unopened chests
      unopenedChests: {
        items: unopenedChests,
        total: unopenedChestsCount,
      },

      // Parsed chests for display
      parsedChests: chests,
    });
  }, [
    address,
    seiBalance,
    dailyChestData,
    purchasedToday,
    remainingToday,
    dailyLimit,
    hasReachedLimit,
    chestTypes,
    isLoading,
    isError,
    unopenedChests,
    unopenedChestsCount,
    chests,
  ]);

  const handleChestClick = (chest: Chest) => {
    console.log('[ShopContent] Chest clicked:', {
      chest,
      dailyLimit: {
        purchasedToday,
        remainingToday,
        total: dailyLimit,
        hasReachedLimit,
      },
      currentBalance: seiBalance,
    });

    // Check if daily limit reached
    if (hasReachedLimit) {
      console.log('[ShopContent] Daily limit reached, showing error toast');
      showToast.error(
        `Daily limit reached! You've purchased ${purchasedToday}/${dailyLimit} chests today. Come back tomorrow!`,
        {
          style: {
            background: '#f44336',
            color: '#fff',
          },
          duration: 5000,
        }
      );
      return;
    }

    setSelectedChest(chest);
    setIsPurchaseModalOpen(true);
  };

  // Handle transaction confirmation with backend polling
  useEffect(() => {
    const alreadyVerified = hash ? verifiedHashes.has(hash) : false;

    const shouldVerify =
      hash &&
      !isPending &&
      !isConfirming &&
      selectedChest &&
      !verifyPurchaseMutation.isPending &&
      !alreadyVerified;

    if (hash) {
      console.log('[ShopContent] Transaction hash status:', {
        hash,
        isPending,
        isConfirming,
        alreadyVerified,
        shouldVerify,
      });
    }

    if (shouldVerify) {
      console.log(
        '[ShopContent] Starting backend verification for hash:',
        hash
      );
      // Close purchase modal before starting verification
      setIsPurchaseModalOpen(false);

      // Start verification polling
      verifyPurchaseMutation.mutate(hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, isConfirming, hash, selectedChest, verifiedHashes]);

  const handlePurchaseConfirm = async () => {
    if (!selectedChest) {
      console.log('[ShopContent] Purchase confirm: No chest selected');
      return;
    }

    // Check network first
    if (!isCorrectNetwork) {
      console.log('[ShopContent] Wrong network, attempting to switch to', requiredNetwork);
      setIsPurchasing(true);

      const switched = await switchToCorrectNetwork();

      if (!switched) {
        console.log('[ShopContent] Failed to switch network');
        showToast.error(
          `Please switch to ${requiredNetwork} to purchase chests`,
          {
            style: {
              background: '#f44336',
              color: '#fff',
            },
            duration: 5000,
          }
        );
        setIsPurchasing(false);
        return;
      }

      console.log('[ShopContent] Successfully switched to', requiredNetwork);
      showToast.success(`Switched to ${requiredNetwork}`, {
        style: {
          background: '#4CAF50',
          color: '#fff',
        },
      });
    }

    const canAfford = seiBalance >= selectedChest.price;

    console.log('[ShopContent] Purchase confirm:', {
      chest: selectedChest,
      userBalance: seiBalance,
      chestPrice: selectedChest.price,
      canAfford,
      network: { isCorrectNetwork, requiredNetwork },
    });

    if (!canAfford) {
      console.log('[ShopContent] Insufficient balance, showing error');
      showToast.error(
        `Insufficient balance! You need ${selectedChest.price} SEI but only have ${seiBalance.toFixed(4)} SEI`,
        {
          style: {
            background: '#f44336',
            color: '#fff',
          },
          duration: 5000,
        }
      );

      setIsPurchaseModalOpen(false);
      setIsPurchasing(false);
      return;
    }

    setIsPurchasing(true);

    try {
      const chestId = selectedChest.id.replace('chest-', '');
      const valueInWei = parseUnits(selectedChest.price.toString(), 18);

      console.log('[ShopContent] Initiating contract write:', {
        chestId,
        chestIdBigInt: BigInt(chestId),
        valueInWei: valueInWei.toString(),
        contractAddress: SHOP_CONTRACT_ADDRESS,
      });

      writeContract({
        address: SHOP_CONTRACT_ADDRESS,
        abi: SHOP_CONTRACT_ABI,
        functionName: 'buyChest',
        args: [BigInt(chestId)],
        value: valueInWei,
      });
    } catch (error) {
      console.error('[ShopContent] Purchase error:', error);
      showToast.error('Purchase failed. Please try again.', {
        style: {
          background: '#f44336',
          color: '#fff',
        },
      });
      setIsPurchasing(false);
    }
  };

  if (isLoading) {
    return <Loading text="Loading chests" size="lg" fullScreen />;
  }

  if (isError) {
    return (
      <div className="w-full min-h-full relative overflow-hidden flex flex-col items-center justify-center">
        <p className="text-red-400">Failed to load chests from contract</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative overflow-hidden flex flex-col">
      {/* Fixed Header */}
      <div className="fixed top-[130px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto flex flex-col items-center px-3 gap-3 z-40 pb-4">
        {/* Inventory Icon - Top Right (inside container) */}
        <div className="absolute top-0 right-4">
          <motion.div
            className="relative"
            animate={{
              y: [0, -4, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            {/* Glowing rings */}
            <motion.div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                width: '56px',
                height: '56px',
                background:
                  'radial-gradient(circle, rgba(250, 204, 21, 0.2) 0%, rgba(168, 85, 247, 0.1) 50%, transparent 70%)',
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            {/* Sparkle dots */}
            <motion.div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ width: '60px', height: '60px' }}
            >
              {[0, 120, 240].map((angle, idx) => (
                <motion.div
                  key={angle}
                  className="absolute left-1/2 top-0 w-1.5 h-1.5 bg-yellow-300 rounded-full"
                  style={{
                    marginLeft: '-3px',
                    transformOrigin: '3px 30px',
                    transform: `rotate(${angle}deg)`,
                    boxShadow: '0 0 4px rgba(250, 204, 21, 0.8)',
                  }}
                  animate={{
                    scale: [0.8, 1.2, 0.8],
                    opacity: [0.6, 1, 0.6],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: idx * 0.5,
                  }}
                />
              ))}
            </motion.div>

            <motion.button
              className="relative p-2 rounded-full z-10 cursor-pointer"
              style={{
                filter: 'drop-shadow(0 0 8px rgba(250, 204, 21, 0.4))',
              }}
              onClick={() => setIsInventoryModalOpen(true)}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.95 }}
            >
              <img src={inventory} alt="Inventory" className="size-8" />
              {/* Unopened Chests Badge */}
              {unopenedChestsCount > 0 && (
                <motion.div
                  className="absolute bottom-0 right-0 left-0 mx-auto bg-red-500 text-white text-xs font-bold rounded-full size-5 flex items-center justify-center px-1 border-2 border-white shadow-lg"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                >
                  {unopenedChestsCount}
                </motion.div>
              )}
            </motion.button>
          </motion.div>
        </div>

        {/* Header Container */}
        <motion.div
          className="flex flex-col items-center p-0 w-[335px] gap-2 mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header Title */}
          <h1
            className="w-full font-normal text-[38px] leading-[43px] text-center text-white"
            style={{
              WebkitTextStroke: '1px #000',
              textShadow: '1px 1px 0px #000',
            }}
          >
            Shop
          </h1>

          {/* Invite Container */}
          <div
            className="flex flex-col items-center p-0 gap-1"
            style={{
              // WebkitTextStroke: '0.5px #000',
              textShadow: '0.5px 1px 0px #000',
            }}
          >
            <p className="font-normal text-sm leading-4 text-white">
              Chests for sale, fortunes inside
            </p>
            <p className="font-normal text-sm leading-4 text-white">
              Spend your SEI, gear up for the fight
            </p>
          </div>

          {/* Daily Limit Progress */}
          <motion.div
            className="flex flex-col items-center gap-2 w-full max-w-[280px] px-4 py-2.5 bg-black/30 backdrop-blur-sm rounded-xl border border-white/20"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            {/* Title */}
            <div className="flex items-center justify-between w-full">
              <span
                className="font-lilita text-sm text-white"
                style={{
                  textShadow: '0.5px 1px 0px #000',
                }}
              >
                Daily Purchase Limit
              </span>
              <span
                className={`font-lilita text-sm ${
                  hasReachedLimit ? 'text-red-400' : 'text-yellow-400'
                }`}
                style={{
                  textShadow: '0.5px 1px 0px #000',
                }}
              >
                {purchasedToday}/{dailyLimit}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden border border-white/10">
              <motion.div
                className={`h-full ${
                  hasReachedLimit
                    ? 'bg-gradient-to-r from-red-500 to-red-600'
                    : 'bg-gradient-to-r from-yellow-400 to-orange-500'
                }`}
                initial={{ width: 0 }}
                animate={{
                  width: `${(purchasedToday / dailyLimit) * 100}%`,
                }}
                transition={{ duration: 0.6, delay: 0.4 }}
              />
            </div>

            {/* Status Text */}
            {hasReachedLimit ? (
              <p
                className="font-lilita text-xs text-red-300 text-center"
                style={{
                  textShadow: '0.5px 1px 0px #000',
                }}
              >
                Come back tomorrow for more!
              </p>
            ) : (
              <p
                className="font-lilita text-xs text-white/80 text-center"
                style={{
                  textShadow: '0.5px 1px 0px #000',
                }}
              >
                {remainingToday} chest{remainingToday !== 1 ? 's' : ''}{' '}
                remaining today
              </p>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Scrollable Content */}
      <motion.div
        className="fixed top-[320px] bottom-0 pb-[100px] left-0 right-0 max-w-[var(--app-max-width)] mx-auto px-3 overflow-y-auto z-30 scrollbar-thin scrollbar-thumb-purple-500 scrollbar-track-purple-900/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {/* Chest Section */}
        <div className="flex flex-col items-center p-0 relative w-full bg-[#00000060] backdrop-blur-sm rounded-2xl pt-3 px-1.5 pb-5 gap-4">
          {/* Section Title */}
          <h2 className="w-full font-normal text-2xl leading-[27px] text-center text-white">
            Chest
          </h2>

          {/* Chest List Container */}
          <div className="flex flex-col items-start p-0 w-full gap-4 px-3 pb-3">
            {chests.length > 0 ? (
              <>
                {/* Row 1 - First 3 chests */}
                {chests.slice(0, 3).length > 0 && (
                  <div className="flex flex-row items-start p-0 w-full gap-2">
                    {chests.slice(0, 3).map((chest) => (
                      <ChestCard
                        key={chest.id}
                        chest={chest}
                        onClick={() => handleChestClick(chest)}
                        // disabled={hasReachedLimit}
                      />
                    ))}
                  </div>
                )}

                {/* Row 2 - Last chest */}
                {chests.length > 3 && (
                  <div className="w-full">
                    <ChestCard
                      chest={chests[chests.length - 1]}
                      onClick={() =>
                        handleChestClick(chests[chests.length - 1])
                      }
                      isLast={true}
                      // disabled={hasReachedLimit}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="w-full text-center py-8">
                <p className="text-white">No chests available</p>
              </div>
            )}
          </div>

        </div>

      </motion.div>

      {/* Purchase Chest Modal */}
      <PurchaseChestModal
        isOpen={isPurchaseModalOpen}
        chest={selectedChest}
        onClose={() => {
          setIsPurchaseModalOpen(false);
          setIsPurchasing(false);
        }}
        onConfirm={handlePurchaseConfirm}
        isLoading={
          isPending ||
          isConfirming ||
          isPurchasing ||
          isSwitching ||
          verifyPurchaseMutation.isPending
        }
      />

      {/* Inventory Modal */}
      <InventoryModal
        isOpen={isInventoryModalOpen}
        onClose={() => setIsInventoryModalOpen(false)}
        unopenedChests={unopenedChests}
        onChestOpened={refetchUnopenedChests}
      />

      <VerificationLoadingModal isOpen={verifyPurchaseMutation.isPending} />
    </div>
  );
};
